import { Injectable } from "@angular/core";
import { LoginService } from "./login.service";
import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { catchError, firstValueFrom } from "rxjs";
import { BACKEND_URI } from "../config/config-vars";
import { Game } from "../models/game.model";
import { BoardActivation } from "../models/boardActivation.model";

const BASE_URL = `${BACKEND_URI}/api/boardactivation`;
const GET_ALL_URL = BASE_URL;
const INSERT_URL = BASE_URL;
const UPDATE_URL = (id) => `${BASE_URL}/${id}`;
const GET_BY_ID_URL = (id) => `${BASE_URL}/${id}`;
const DELETE_BY_ID_URL = (id) => `${BASE_URL}/${id}`;

interface ActivationFetchOptions {
  pageSize?: number;
  maxPages?: number;
  cutoffDate?: Date;
  forceRefresh?: boolean;
}


@Injectable({providedIn: 'root'})
export class boardActivationService {
    constructor(private http: HttpClient, private loginService: LoginService){}
    private cachedActivations: BoardActivation[] | null = null;
    private inFlightFetch: Promise<BoardActivation[]> | null = null;
    
    async getAll(options: ActivationFetchOptions = {}): Promise<BoardActivation[]> {
        const { pageSize = 200, maxPages = 50, cutoffDate, forceRefresh = false } = options;

        if(!this.loginService.isloggedIn) {
            console.error('User is not logged in');
            return [];
        }

        const useCache = !cutoffDate && !forceRefresh;

        if (useCache && this.cachedActivations) {
            return this.cachedActivations;
        }

        if (useCache && this.inFlightFetch) {
            return this.inFlightFetch;
        }

        const fetchPromise = this.fetchPagedActivations(pageSize, maxPages, cutoffDate);
        if (useCache) {
            this.inFlightFetch = fetchPromise;
        }

        try {
            const data = await fetchPromise;
            if (useCache) {
                this.cachedActivations = data;
            }
            return data;
        } catch (error) {
            console.error('Error in getAll():', error);
            return useCache ? (this.cachedActivations || []) : [];
        } finally {
            if (useCache) {
                this.inFlightFetch = null;
            }
        }
    }

    private async fetchPagedActivations(pageSize: number, maxPages: number, cutoffDate?: Date): Promise<BoardActivation[]> {
        const all: BoardActivation[] = [];
        let page = 0;
        const cutoffTs = cutoffDate ? cutoffDate.getTime() : null;

        while (page < maxPages) {
            const url = `${GET_ALL_URL}?page=${page}&pageSize=${pageSize}`;

            const response = await firstValueFrom(
                this.http.get<BoardActivation[]>(url)
                    .pipe(
                        catchError((error: HttpErrorResponse) => {
                            console.error(`Error fetching activations page ${page}:`, error);
                            throw error;
                        })
                    )
            );

            const items = response || [];
            const filtered = cutoffTs
                ? items.filter(item => {
                    const ts = Date.parse(item?.timeStamp || '');
                    return Number.isFinite(ts) ? ts >= cutoffTs : true;
                  })
                : items;

            all.push(...filtered);

            // Assume backend returns newest -> oldest; stop once the last item is older than cutoff
            const reachedCutoff = cutoffTs
                ? (() => {
                    const last = items[items.length - 1];
                    const ts = Date.parse(last?.timeStamp || '');
                    return Number.isFinite(ts) ? ts < cutoffTs : false;
                  })()
                : false;

            if (items.length < pageSize || reachedCutoff) {
                break;
            }

            page += 1;
        }

        return all;
    }

    // async getById(id): Promise<Game> {
    //     if(this.loginService.isloggedIn) {
    //         return await firstValueFrom(
    //             this.http.get<Game>(GET_BY_ID_URL(id))
    //         );
    //     }
    // }
    // async create(data: Game): Promise<Game> {
    //     if(this.loginService.isloggedIn) {
    //         return await firstValueFrom(
    //             this.http.post<Game>(INSERT_URL, data)
    //         );
    //     }
    // }
    // async update(id, data: Game): Promise<Game> {
    //     if(this.loginService.isloggedIn) {
    //         return await firstValueFrom(
    //             this.http.put<Game>(UPDATE_URL(id), data)
    //         );
    //     }
    // }

    // async delete(id): Promise<void> {
    //     if(this.loginService.isloggedIn) {
    //         try{
    //             await firstValueFrom(
    //                 this.http.delete(DELETE_BY_ID_URL(id))
    //             );

    //         } catch(err) {}            
    //     }
    //   }
}
